import {normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";

import {buildDaysFromRange} from "../lib/build-days-from-range";
import type {
  CreateSafelistingRequest,
  SafelistingCreateContext,
  SafelistingFormValues,
  UpdateSafelistingRequest,
} from "../types";

/**
 * Converts form values and context to the API create request payload.
 * Throws if household or dates_range is missing (form should validate before submit).
 */
export function formValuesToCreateRequest(
  formValues: SafelistingFormValues,
  context: SafelistingCreateContext,
): CreateSafelistingRequest {
  const household = formValues.household;
  const datesRange = formValues.dates_range;

  if (household == null) {
    throw new Error("Household is required");
  }
  if (!datesRange?.from || !datesRange?.to) {
    throw new Error("Date range with from and to is required");
  }

  const days = buildDaysFromRange({
    dates_range: {from: datesRange.from, to: datesRange.to},
    excluded_dates: formValues.excluded_dates,
  });

  const recipientPhone = normalizeUsPhoneDigits(formValues.recipient_phone);

  const request: CreateSafelistingRequest = {
    vehicle_id: context.vehicle_id,
    context_property_id: context.context_property_id,
    household_id: household.house_hold_id,
    type: context.type,
    days,
    recipient_name: formValues.recipient_name.trim() || undefined,
    recipient_email: formValues.recipient_email.trim() || undefined,
  };

  if (!context.omitOverrideFields) {
    request.override_count_toward_total = formValues.override_count_toward_total;
    request.override_days_in_a_row = formValues.override_days_in_a_row;
    request.override_limit = formValues.override_limit;
    if (formValues.override_limit && formValues.override_reason?.trim()) {
      request.override_reason = formValues.override_reason.trim();
    }
  }

  if (recipientPhone) request.recipient_phone = recipientPhone;

  if (!context.omitOverrideFields) {
    if (formValues.notes?.trim()) request.notes = formValues.notes.trim();
    if (formValues.private_notes?.trim()) {
      request.private_notes = formValues.private_notes.trim();
    }
  }

  return request;
}

/**
 * Converts form values and context to the API update request payload (partial).
 */
export function formValuesToUpdateRequest(
  formValues: SafelistingFormValues,
  context: SafelistingCreateContext,
): UpdateSafelistingRequest {
  const request = formValuesToCreateRequest(formValues, context);
  // PATCH: omit leaves unchanged; empty string clears stored phone.
  request.recipient_phone = normalizeUsPhoneDigits(formValues.recipient_phone);
  return request;
}
